import React, { Component } from 'react';
import { Row, Col } from 'react-bootstrap';
import moment from 'moment';
import { confirmAlert } from 'react-confirm-alert';
import { inject, observer } from 'mobx-react';

//components
import WorkPatternPreview from './WorkPatternPreview';
import WorkPatternHistoryModal from '../WorkPatternHistoryModal';
import ConflictCyclesModal from './ConflictCyclesModal';
import { Textarea } from '../../../../elements/Input';
import PatternStartWeekEmployeeSelect from './PatternStartWeekEmployeeSelect';

// Elements
import Select from '../../../../elements/Select';
import DatePicker from '../../../../components/DatePicker/DatePicker';
import { ScrollSelectWidget } from '../../../../components/GenericForm/Widgets/ScrollSelectWidget';
import CustomMultiSelect from '../../../../elements/CustomMultiSelect';
import Button from '../../../../components/CustomButton/CustomButton';
import { CurrentUserGroupsWidget } from '../../../../components/GenericForm/Widgets/UserGroupWidget';
import AutoTimeRecordingDays from '~/components/RosterShiftOptions/AutoTimeRecordingDays';
import RosterOverrideSettings from '~/components/RosterShiftOptions/RosterOverrideSettings';
import { DATE_FORMAT } from '../RosterScheduler';

//utils
import { DEFAULT_VISIBILITY_WEEKS } from '../../../../utils/constants';
import { validateModuleAccess } from '../../../../library/stores/userStore';

//styles
import '../RosterScheduler/css/workPatternStyle.css';

class AssignPattern extends Component {
    constructor(props) {
        super(props);
        this.state = { showVersionHistory: false, cycleWeekOffsets: {}, activeWeek: 1, showConflictModal: false, conflictData: null };
    }

    getIsShortNotice = () => {
        const { parentState } = this.props;
        const visibilityDays = (parentState.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS) * 7;
        return parentState.assignmentStartDate && moment(parentState.assignmentStartDate).isBefore(moment().add(visibilityDays, 'days').startOf('day'));
    };

    handleConflictConfirm = () => {
        this.setState({ showConflictModal: false, conflictData: null });
        this.executeAssignment(true);
    };

    handleWeekChange = (selectedIds) => {
        const { handleInputChange } = this.props;
        const { cycleWeekOffsets, activeWeek } = this.state;
        const newOffsets = Object.assign({}, cycleWeekOffsets);

        Object.keys(newOffsets).forEach(id => {
            if (newOffsets[id] === activeWeek && !selectedIds.includes(+id)) {
                delete newOffsets[id];
            }
        });
        selectedIds.forEach(id => {
            newOffsets[id] = activeWeek;
        });

        this.setState({ cycleWeekOffsets: newOffsets });
        handleInputChange('assignmentUserIds', Object.keys(newOffsets).map(Number));
    };

    executeAssignment = async (force = false) => {
        const { t, commonStore, rosterStore, parentState, onAssignSuccess, userStore } = this.props;
        const { selectedWorkPattern, assignmentUserIds, assignmentStartDate, assignmentEndDate, overrideComment, nextTemplateId, autoTimesheetDays, overrideShift, overrideAutoTimeRecording } = parentState;
        const { cycleWeekOffsets } = this.state;

        const isShortNotice = this.getIsShortNotice();
        let pmMembers = [];
        if (userStore.currentUser?.user_type !== 'admin' && userStore.currentUser?.Manages?.length) {
            pmMembers = userStore.currentUser.Manages;
        }
        try {
            const response = await rosterStore.assignWorkPattern({
                template_id: selectedWorkPattern,
                user_ids: assignmentUserIds,
                start_date: assignmentStartDate,
                pm_members: pmMembers || [],
                pattern_start_weeks: cycleWeekOffsets,
                overrideShift,
                overrideAutoTimeRecording,
                ...(autoTimesheetDays ? { autoTimesheetDays } : {}),
                ...(assignmentEndDate && { end_date: assignmentEndDate }),
                ...(nextTemplateId && { next_template_id: nextTemplateId }),
                ...(isShortNotice && { override_comments: overrideComment }),
                ...(force && { force: true }),
            });
            commonStore.addNotification(response.message || t('Created Successfully'), null, 'success');
            this.setState({ cycleWeekOffsets: {} });
            onAssignSuccess();
        } catch (err) {
            const body = err?.response?.body;
            if (body?.hasConflicts) {
                const truncations = (body.employees || []).filter(c => c.action === 'truncate');
                const deletions = (body.employees || []).filter(c => c.action === 'delete');
                const hasPublished = body.hasPublishedCycles;
                this.setState({ showConflictModal: true, conflictData: { truncations, deletions, hasPublished } });
            } else {
                const msg = err?.response?.body?.errors?.message || err?.response?.body?.error || t('Something went wrong');
                commonStore.addNotification(msg, null, 'error');
            }
        }
    };

    onSave = () => {
        const { t, parentState, commonStore } = this.props;
        const dateTimeRule = commonStore.getDateTimeRules();
        const { selectedWorkPattern, assignmentUserIds, assignmentStartDate, assignmentEndDate, nextTemplateId, overrideComment } = parentState;

        const isShortNotice = this.getIsShortNotice();

        if (!selectedWorkPattern || !assignmentUserIds.length || !assignmentStartDate) {
            return commonStore.addNotification(t('Please fill in all required fields'), null, 'error');
        }
        if (isShortNotice && !overrideComment.trim()) {
            return commonStore.addNotification(t('Please provide a reason for the short notice change.'), null, 'error');
        }

        if (assignmentEndDate && !nextTemplateId) {
            confirmAlert({
                title: t('Are You Sure'),
                message: t('No work pattern is set after') + ` ${moment(assignmentEndDate).format(dateTimeRule.short_date_format)}. ` + t('Users may be left without a schedule. Do you want to continue?'),
                buttons: [
                    {
                        label: t('Yes, Proceed'),
                        onClick: () => this.executeAssignment()
                    },
                    {
                        label: t('Cancel'),
                        onClick: () => { }
                    }
                ],
                closeOnEscape: true,
                closeOnClickOutside: true,
            });
        } else {
            this.executeAssignment();
        }
    };

    render() {
        const { t, parentState, handleInputChange, handleWorkPatternSelect, handleClose, router, commonStore, userStore, workPatternLoadFn } = this.props;
        const {
            departmentOptions, selectedDepartment,
            selectedWorkPattern, filteredEmployees, assignedUserIds,
            assignmentStartDate, assignmentEndDate, overrideComment, user_group, workPatternDetails,
            cycleWeekLength, autoTimesheetDays, overrideShift, overrideAutoTimeRecording
        } = parentState;
        const { currentUser } = userStore;
        const { cycleWeekOffsets, activeWeek, showVersionHistory, showConflictModal, conflictData } = this.state;

        const clientData = commonStore?.config?.client?.data;
        const allowOverride = !!clientData?.basicRules?.workPlanSettings?.allow_optional_roster_shift_override;
        const allowShiftAutoTimesheetToggle = !!clientData?.loginRules?.allowShiftToEnableDisableAutoTimesheet;

        const assignedSet = new Set(assignedUserIds || []);
        const totalWeeks = (workPatternDetails && workPatternDetails.total_weeks) || 1;
        const weekCounts = {};
        for (let w = 1; w <= totalWeeks; w++) weekCounts[w] = 0;
        Object.values(cycleWeekOffsets).forEach(w => { if (weekCounts[w] !== undefined) weekCounts[w]++; });

        const employeeOptions = (filteredEmployees || []).map(e => ({
            value: e.id,
            label: e.name,
            disabled: !!cycleWeekOffsets[e.id] && cycleWeekOffsets[e.id] !== activeWeek,
            _label: cycleWeekOffsets[e.id] && cycleWeekOffsets[e.id] !== activeWeek ? `${t('Start')} ${t('Week')} ${cycleWeekOffsets[e.id]}` : assignedSet.has(e.id) ? t('Assigned') : '',
        }));
        const weekValue = Object.entries(cycleWeekOffsets).filter(([, w]) => w === activeWeek).map(([id]) => +id);

        const isShortNotice = this.getIsShortNotice();
        const dateTimeRule = commonStore.getDateTimeRules();
        return (
            <div className='d-flex gap-4 pt-2'>
                <div className="assign-pattern-form">
                    <Row className='mb-3'>
                        <Col>
                            <label className='fw-600 fs13'>{t('Department')}</label>
                            <Select options={departmentOptions} value={departmentOptions.find(o => o.value === selectedDepartment)} onChange={o => handleInputChange('selectedDepartment', o?.value)} />
                            {cycleWeekLength && (
                                <small className='text-muted mt-1 d-block'>
                                    {t('Cycle Duration (Weeks)')}: <span className='fw-600'>{cycleWeekLength}</span>
                                </small>
                            )}
                        </Col>
                        <Col>
                            <label className='fw-600 fs13'>{t('Select Group')}</label>
                            <CurrentUserGroupsWidget value={user_group} onChange={v => handleInputChange('user_group', v)} />
                        </Col>
                    </Row>
                    <div className='mb-2'>
                        <div className='d-flex justify-content-between align-items-center mb-1'>
                            <label className='fw-600 fs13'>{t('Select')} {t('Template')} <span className="text-danger">*</span></label>
                            {(validateModuleAccess('md_WorkPattern', 'manageWorkPatterns')) && selectedWorkPattern && (
                                <span
                                    className='fs12 fw-600 cursor-pointer text-primary-color'
                                    onClick={() => router.navigate('/admin/roster/work-pattern', { state: { editTemplateId: selectedWorkPattern } })}
                                >
                                    <i className='fa fa-pencil me-1' />
                                    {t('Edit') + ' ' + t('Template')}
                                </span>
                            )}
                        </div>
                        <ScrollSelectWidget
                            loadFn={workPatternLoadFn}
                            prepareOption={row => ({ value: row.id, label: row.name, automation: row.automation, department: row.department })}
                            value={selectedWorkPattern}
                            onChange={(val, option) => {
                                if (val && parentState.nextTemplateId && val === parentState.nextTemplateId) {
                                    return commonStore.addNotification(t('The current template cannot be the same as the next template'), null, 'warning');
                                }
                                this.setState({ cycleWeekOffsets: {}, activeWeek: 1 });
                                handleInputChange('assignmentUserIds', []);
                                handleWorkPatternSelect(option);
                            }}
                            cacheKey={selectedDepartment}
                            placeholder={t('Select Template')}
                        />
                        {workPatternDetails?.automation && (
                            <small className='text-muted mt-1 d-block'>
                                {t('auto')}-{t('Publish')}: <span className='fw-600'>{parentState.visibilityWeeks || DEFAULT_VISIBILITY_WEEKS} {t('weeks in advance')}</span>
                            </small>
                        )}
                    </div>
                    <div className='mb-3'>
                        <label className='fw-600 fs13 d-block mb-1'>{t('Select Employees')} <span className="text-danger">*</span></label>
                        <PatternStartWeekEmployeeSelect
                            weekCount={totalWeeks}
                            activeWeek={activeWeek}
                            onWeekTabClick={(w) => this.setState({ activeWeek: w })}
                            weekCounts={weekCounts}
                            t={t}
                        />
                        <CustomMultiSelect
                            options={employeeOptions}
                            value={weekValue}
                            onChange={ids => this.handleWeekChange(ids)}
                            customOptions
                        />
                    </div>
                    <Row className='mb-3'>
                        <Col>
                            <label className='fw-600 fs13'>{t('Start')} {t('Date')} <span className="text-danger">*</span></label>
                            <DatePicker
                                selected={assignmentStartDate ? moment(assignmentStartDate).toDate() : null}
                                onChange={(date) => handleInputChange('assignmentStartDate', date ? moment(date).format(DATE_FORMAT) : null)}
                                minDate={new Date(moment().add(1, 'days'))}
                                bgGrey
                            />
                        </Col>
                        <Col>
                            <label className='fw-600 fs13'>{t('Expiration')}</label>
                            <DatePicker
                                selected={assignmentEndDate ? moment(assignmentEndDate).toDate() : null}
                                onChange={(date) => handleInputChange('assignmentEndDate', date ? moment(date).format(DATE_FORMAT) : null)}
                                minDate={assignmentStartDate ? moment(assignmentStartDate).add(1, 'day').toDate() : moment().add(1, 'day').toDate()}
                                bgGrey
                                isClearable
                            />
                        </Col>
                    </Row>
                    {allowShiftAutoTimesheetToggle && (
                        <>
                            <hr />
                            <AutoTimeRecordingDays
                                autoTimesheetDays={autoTimesheetDays}
                                onChange={(v) => handleInputChange('autoTimesheetDays', v)}
                            />
                            <hr />
                        </>
                    )}
                    {allowOverride && (
                        <RosterOverrideSettings
                            overrideShift={overrideShift}
                            overrideAutoTimeRecording={overrideAutoTimeRecording}
                            onChange={({ overrideShift: os, overrideAutoTimeRecording: oa }) => {
                                handleInputChange('overrideShift', os);
                                handleInputChange('overrideAutoTimeRecording', oa);
                            }}
                        />
                    )}
                    {assignmentEndDate && (
                        <div className='mb-3 animate-fade-in'>
                            <label className='fw-600 fs13'>{t('Next Template')} ({t('After Expiration')})</label>
                            <ScrollSelectWidget
                                loadFn={workPatternLoadFn}
                                prepareOption={row => ({ value: row.id, label: row.name, automation: row.automation, department: row.department })}
                                value={parentState.nextTemplateId}
                                onChange={(val, option) => {
                                    if (val && val === selectedWorkPattern) {
                                        return commonStore.addNotification(t('The next template cannot be the same as the current template'), null, 'warning');
                                    }
                                    handleInputChange('nextTemplateId', val);
                                }}
                                cacheKey={selectedDepartment}
                                placeholder={t('Select a template to start next')}
                            />
                            <small className="text-muted">
                                {t('This pattern will automatically activate on')} {moment(assignmentEndDate).add(1, 'day').format(dateTimeRule.short_date_format)}
                            </small>
                        </div>
                    )}
                    {isShortNotice && (
                        <div className='mb-3'>
                            <label className='form-label fw-600 fs13'>{t('Reason for Short Notice Change')}<span className="text-danger">*</span></label>
                            <Textarea
                                rows={5}
                                className="h-auto"
                                value={overrideComment}
                                onChange={e => handleInputChange('overrideComment', e)}
                            />
                        </div>
                    )}
                    <div className='d-flex gap-2 justify-content-end mt-4'>
                        <Button onClick={handleClose}>{t('Cancel')}</Button>
                        <Button fill onClick={this.onSave}>{t('Assign')}</Button>
                    </div>
                </div>
                <div className="vr" />
                <div className='flex-grow-1'>
                    {selectedWorkPattern && (
                        <div className="d-flex justify-content-between align-items-start px-3 mb-2">
                            <div>
                                <small className="text-muted d-block">
                                    <i className="fa fa-eye me-1" />
                                    {t('This preview shows the latest version of the work pattern.')}
                                </small>
                                <small className="text-muted d-block mt-1">
                                    <i className="fa fa-info-circle me-1" />
                                    {t('Template Cycles will be generated based on the version active between its start and end dates.')}
                                </small>
                                {workPatternDetails?.has_standby_code && (
                                    <small className="text-danger d-block mt-1">
                                        <i className="fa fa-info-circle me-1" />
                                        {t('If urgent work is already registered on a day assigned as standby, the standby call overrides the existing urgent call entry.')}
                                    </small>
                                )}
                            </div>
                            <Button sm onClick={() => this.setState({ showVersionHistory: true })}>
                                <i className="fa fa-history me-1" />
                                {t('View Versions')}
                            </Button>
                        </div>
                    )}
                    <WorkPatternPreview
                        t={t}
                        workPatternDetails={workPatternDetails}
                        isLoading={parentState.isLoadingDetails}
                        companyShiftMap={commonStore.companyShiftMap}
                    />
                    <WorkPatternHistoryModal
                        show={showVersionHistory}
                        handleClose={() => this.setState({ showVersionHistory: false })}
                        templateId={selectedWorkPattern}
                    />
                </div>
                <ConflictCyclesModal
                    show={showConflictModal}
                    onHide={() => this.setState({ showConflictModal: false, conflictData: null })}
                    onConfirm={this.handleConflictConfirm}
                    data={conflictData}
                    confirmLabel={t('Confirm') + ' & ' + t('Assign')}
                    t={t}
                />
            </div>
        );
    }
}

export default inject('rosterStore', 'commonStore', 'userStore')(observer(AssignPattern));